import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { setupProjectInterceptor } from "../../../api/api";

const SELECTED_PROJECT_KEY_PREFIX = "vizops:selected-project:v1";
const DIAGRAM_LAYOUT_KEY_PREFIX = "vizops:diagram-layout:v1";
const NAMESPACED_PREFIXES = [
  SELECTED_PROJECT_KEY_PREFIX,
  DIAGRAM_LAYOUT_KEY_PREFIX,
];

const storageKey = (userId) =>
  userId ? `${SELECTED_PROJECT_KEY_PREFIX}:${userId}` : null;

// Drop any namespaced entries that don't belong to the current user.
// Keeps localStorage from growing unbounded on shared machines.
const sweepOtherUsers = (currentUserId) => {
  if (!currentUserId) return;
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const matchedPrefix = NAMESPACED_PREFIXES.find((p) =>
        key.startsWith(`${p}:`)
      );
      if (!matchedPrefix) continue;
      const owner = key.slice(matchedPrefix.length + 1);
      if (owner && owner !== currentUserId) toRemove.push(key);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
};

const readStoredProject = (userId) => {
  const key = storageKey(userId);
  if (!key) return null;
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return null;
  }
};

const writeStoredProject = (userId, projectId) => {
  const key = storageKey(userId);
  if (!key) return;
  try {
    if (projectId) localStorage.setItem(key, projectId);
    else localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const useOverviewStore = create(
  devtools((set, get) => ({
    services: [],
    versions: [],
    loading: false,
    refreshing: false,
    selectedVersion: null,
    selectedProject: null,
    userId: null,
    endpointsByService: {},
    endpointLoadingFor: null,
    filters: { platforms: [], serviceTypes: [], tags: [] },

    setServices: (services) => set({ services }),
    setFilters: (filters) => set({ filters }),
    resetFilters: () => set({ filters: { platforms: [], serviceTypes: [], tags: [] } }),
    setVersions: (versions) => set({ versions }),
    setLoading: (loading) => set({ loading }),
    setRefreshing: (refreshing) => set({ refreshing }),
    setSelectedVersion: (versionId) => set({ selectedVersion: versionId }),
    setUserId: (userId) => {
      const current = get().userId;
      if (current === userId) return;
      // Account changed (or first load) — purge other users' namespaced
      // entries, then rehydrate from this user's namespace.
      sweepOtherUsers(userId);
      const restored = readStoredProject(userId);
      if (restored) setupProjectInterceptor(restored);
      set({
        userId,
        selectedProject: restored,
        endpointsByService: {},
      });
    },
    setSelectedProject: (projectId) => {
      const { userId } = get();
      setupProjectInterceptor(projectId);
      writeStoredProject(userId, projectId);
      set({
        selectedProject: projectId,
        endpointsByService: {},
      });
    },
    setServiceEndpoints: (serviceName, data) =>
      set((state) => ({
        endpointsByService: { ...state.endpointsByService, [serviceName]: data },
      })),
    setEndpointLoadingFor: (serviceName) =>
      set({ endpointLoadingFor: serviceName }),
  }))
);
export default useOverviewStore;
